import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format, isToday, isTomorrow } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Search,
  Calendar,
  ArrowRight,
  ChevronRight,
  MapPin,
  Sparkles,
} from "lucide-react";
import { ChefHat } from "@/components/svg/ChefHat";
import { CustomRequestStore } from "@/stores/customRequestStore";
import { useSession } from "@/hooks/useSession";
import { bookingQueries } from "@/domain/booking/booking.query";
import { chefQueries } from "@/domain/chef/chef.query";
import { ChefLiveCard } from "@/components/marketplace/ChefLiveCard";
import { Image } from "@/components/ui/image";
import { BookingStatuses, OrderBy } from "@hometoq/backend/enums";
import { getImageUrl } from "@/lib/image";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/client/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { setOpen } = CustomRequestStore();
  const { user } = useSession();
  const firstName =
    (user as any)?.customer?.firstName ??
    (user as any)?.firstName ??
    (user as any)?.email?.split("@")[0] ??
    "";

  const { data: upcomingData } = useQuery(
    bookingQueries.list({
      name: "",
      priceSort: null,
      dateSort: OrderBy.ASC,
      date: null,
      statusSort: null,
      status: BookingStatuses.ACCEPTED,
      page: 1,
    }),
  );
  const { data: finishedData } = useQuery(
    bookingQueries.list({
      name: "",
      priceSort: null,
      dateSort: OrderBy.DESC,
      date: null,
      statusSort: null,
      status: BookingStatuses.FINISHED,
      page: 1,
    }),
  );

  const nextBooking = (upcomingData?.data?.[0] as any) ?? null;
  const completedCount = finishedData?.data?.length ?? 0;

  const { data: nearbyChefs, isLoading: isLoadingChefs } = useQuery(
    chefQueries.chefsFirstPage({}),
  );

  return (
    <section className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      <Hero
        firstName={firstName}
        nextBooking={nextBooking}
        completedCount={completedCount}
      />

      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <NextBookingCard booking={nextBooking} />
        </div>
        <div className="lg:col-span-2">
          <QuickActions onCustomRequest={() => setOpen(true)} />
        </div>
      </div>

      <NearbyChefs
        chefs={nearbyChefs?.data?.slice(0, 6) ?? []}
        isLoading={isLoadingChefs}
      />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Hero
// ─────────────────────────────────────────────────────────────────────────

function Hero({
  firstName,
  nextBooking,
  completedCount,
}: {
  firstName: string;
  nextBooking: any | null;
  completedCount: number;
}) {
  let subtitle: string;
  if (nextBooking) {
    const date = new Date(nextBooking.date);
    const label = isToday(date)
      ? "aujourd'hui"
      : isTomorrow(date)
        ? "demain"
        : `le ${format(date, "EEEE d MMMM", { locale: fr })}`;
    subtitle = `Votre prochaine prestation est prévue ${label} avec ${nextBooking.chef?.firstName ?? "votre chef"}.`;
  } else if (completedCount > 0) {
    subtitle = `Vous avez ${completedCount} prestation${completedCount > 1 ? "s" : ""} à votre actif. Prêt pour la prochaine ?`;
  } else {
    subtitle =
      "Trouvez le chef parfait pour votre prochain événement, ou recevez des suggestions sur-mesure.";
  }

  return (
    <header className="relative overflow-hidden rounded-3xl border border-primary/10 p-6 md:p-8 bg-gradient-to-br from-salmon/45 via-[#fbf6f3] to-white">
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/3 w-40 h-40 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

      <div className="relative flex flex-col lg:flex-row lg:items-center gap-5 justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/80">
              Espace client
            </span>
            <span className="w-1 h-1 rounded-full bg-primary" />
            <span className="text-[11px] text-muted-foreground capitalize">
              {format(new Date(), "EEEE d MMMM", { locale: fr })}
            </span>
          </div>
          <h1 className="font-h1 text-dark-blue mb-1">
            Bonjour {firstName}
          </h1>
          <p className="text-sm text-muted-foreground max-w-md">{subtitle}</p>
        </div>

        {nextBooking ? (
          <Link
            to="/dashboard/client/reservations"
            className="group inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-primary text-white shadow-[0_8px_24px_-8px_rgba(232,89,60,0.5)] hover:shadow-[0_8px_28px_-6px_rgba(232,89,60,0.65)] transition-shadow shrink-0"
          >
            <span className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
              <Calendar size={18} />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                À venir
              </span>
              <span className="text-sm font-semibold">
                Voir mes réservations
              </span>
            </span>
            <ArrowRight
              size={18}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </Link>
        ) : (
          <Link
            to="/trouver-un-chef/chefs"
            className="group inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-primary text-white shadow-[0_8px_24px_-8px_rgba(232,89,60,0.5)] hover:shadow-[0_8px_28px_-6px_rgba(232,89,60,0.65)] transition-shadow shrink-0"
          >
            <span className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
              <Search size={18} />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                Découvrir
              </span>
              <span className="text-sm font-semibold">Trouver un chef</span>
            </span>
            <ArrowRight
              size={18}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </Link>
        )}
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Next booking card
// ─────────────────────────────────────────────────────────────────────────

function NextBookingCard({ booking }: { booking: any | null }) {
  if (!booking) {
    return (
      <div className="h-full min-h-[260px] rounded-3xl border-2 border-dashed border-border bg-white p-8 flex flex-col items-center justify-center text-center">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-3">
          <Calendar className="w-6 h-6 text-primary" />
        </div>
        <h3 className="font-h4 text-dark-blue mb-1">
          Aucune réservation prévue
        </h3>
        <p className="font-small text-muted-foreground max-w-xs mb-4">
          Trouvez un chef pour votre prochain événement et savourez une cuisine
          d'exception.
        </p>
        <Link
          to="/trouver-un-chef/chefs"
          className="inline-flex items-center gap-1.5 bg-dark-blue hover:bg-primary text-white font-button-small px-4 py-2.5 rounded-xl transition-colors"
        >
          Découvrir nos chefs
          <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  const date = new Date(booking.date);
  const dateLabel = isToday(date)
    ? "Aujourd'hui"
    : isTomorrow(date)
      ? "Demain"
      : format(date, "EEEE d MMMM", { locale: fr });
  const chefAvatar =
    getImageUrl(booking.chef?.avatar) ?? "/images/avatar_1.webp";
  const menuName = booking.menu?.name ?? "Menu sur-mesure";
  const guests = booking.guests ?? 0;
  const total = (booking.bookingPricing?.totalPrice ?? 0) / 100;

  return (
    <div className="h-full rounded-3xl border border-border bg-white overflow-hidden flex flex-col">
      <div className="px-6 pt-5 pb-4 border-b border-border flex items-center justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-0.5">
            Prochaine prestation
          </p>
          <p className="font-h4 text-dark-blue capitalize">{dateLabel}</p>
        </div>
        <span className="bg-primary/10 text-primary font-button-extra-small uppercase tracking-wider px-2.5 py-1 rounded-full">
          Confirmée
        </span>
      </div>

      <div className="p-6 flex-1 flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl overflow-hidden bg-cream shrink-0 border-2 border-white shadow-sm">
            <Image
              src={chefAvatar}
              alt={booking.chef?.firstName ?? "Chef"}
              layout="constrained"
              width={56}
              height={56}
              className="object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-button-extra-small uppercase tracking-wider text-muted-foreground">
              Avec
            </p>
            <p className="font-h4 text-dark-blue leading-tight">
              {booking.chef?.firstName ?? "Chef"}
            </p>
            <p className="font-small text-muted-foreground line-clamp-1">
              {menuName}
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-2 mt-2">
          <div className="bg-cream/40 rounded-xl px-3 py-2">
            <dt className="font-button-extra-small uppercase tracking-wider text-muted-foreground">
              Convives
            </dt>
            <dd className="font-button-small text-dark-blue tabular-nums mt-0.5">
              {guests} {guests > 1 ? "personnes" : "personne"}
            </dd>
          </div>
          <div className="bg-cream/40 rounded-xl px-3 py-2">
            <dt className="font-button-extra-small uppercase tracking-wider text-muted-foreground">
              Total
            </dt>
            <dd className="font-button-small text-dark-blue tabular-nums mt-0.5">
              {total.toFixed(2)} €
            </dd>
          </div>
        </dl>

        <Link
          to="/dashboard/client/reservations"
          className="mt-auto inline-flex items-center justify-center gap-1.5 bg-dark-blue hover:bg-primary text-white font-button-small px-4 py-2.5 rounded-xl transition-colors"
        >
          Voir tous les détails
          <ChevronRight size={14} />
        </Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Quick actions
// ─────────────────────────────────────────────────────────────────────────

function QuickActions({ onCustomRequest }: { onCustomRequest: () => void }) {
  return (
    <div className="h-full flex flex-col gap-3">
      <Link
        to="/trouver-un-chef/chefs"
        className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-dark-blue to-[#0e1a3a] text-white p-5 flex items-start gap-4 hover:shadow-xl transition-shadow"
      >
        <div className="absolute -top-12 -right-12 w-36 h-36 rounded-full bg-primary/25 blur-3xl pointer-events-none" />
        <div className="relative w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0 backdrop-blur-sm">
          <Search size={20} />
        </div>
        <div className="relative flex-1 min-w-0">
          <p className="font-h4 leading-tight">Trouver un chef</p>
          <p className="font-small text-white/70 mt-1 leading-snug">
            Parcourez les chefs disponibles dans votre ville.
          </p>
        </div>
        <ArrowRight
          size={18}
          className="relative shrink-0 transition-transform group-hover:translate-x-0.5"
        />
      </Link>

      <button
        type="button"
        onClick={onCustomRequest}
        className="group relative overflow-hidden rounded-2xl bg-white border border-border hover:border-primary/40 hover:shadow-lg p-5 text-left flex items-start gap-4 transition-all"
      >
        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <ChefHat className="w-5 h-5 fill-primary text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-h4 text-dark-blue leading-tight">
            Demande sur-mesure
          </p>
          <p className="font-small text-muted-foreground mt-1 leading-snug">
            Décrivez votre besoin, on vous propose les meilleurs chefs.
          </p>
        </div>
        <Sparkles
          size={18}
          className="shrink-0 text-primary group-hover:rotate-12 transition-transform"
        />
      </button>

      <Link
        to="/dashboard/client/reservations"
        className="group inline-flex items-center justify-between rounded-xl bg-cream/40 hover:bg-cream/70 px-4 py-3 transition-colors"
      >
        <span className="font-button-small text-dark-blue">
          Mon historique de réservations
        </span>
        <ChevronRight
          size={14}
          className="text-muted-foreground transition-transform group-hover:translate-x-0.5"
        />
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Nearby chefs
// ─────────────────────────────────────────────────────────────────────────

function NearbyChefs({
  chefs,
  isLoading,
}: {
  chefs: any[];
  isLoading: boolean;
}) {
  return (
    <section>
      <header className="flex items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">
            <MapPin size={11} className="inline-block mr-0.5 -mt-0.5" />
            Recommandés
          </p>
          <h2 className="font-h2 text-dark-blue leading-tight">
            Les chefs aux alentours
          </h2>
        </div>
        <Link
          to="/trouver-un-chef/chefs"
          className="font-button-small text-primary hover:underline shrink-0 inline-flex items-center gap-1"
        >
          Voir tout
          <ChevronRight size={14} />
        </Link>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "rounded-3xl bg-white border border-border h-[460px] animate-pulse",
              )}
            />
          ))
        ) : chefs.length > 0 ? (
          chefs.map((chef: any, i: number) => (
            <ChefLiveCard key={chef.id} chef={chef} index={i} />
          ))
        ) : (
          <div className="col-span-full bg-white rounded-3xl border-2 border-dashed border-border p-10 text-center">
            <p className="font-button-medium text-dark-blue">
              Aucun chef trouvé dans votre zone pour l'instant.
            </p>
            <p className="font-small text-muted-foreground mt-1">
              Élargissez votre recherche ou faites une demande personnalisée.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
